import { router, usePage } from '@inertiajs/react'
import {
  Briefcase,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  DatabaseBackup,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  Settings,
  Tags,
  UserCog,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react'
import type { ComponentType } from 'react'

import { PlatanusIsotype } from '@/components/icons/PlatanusIsotype'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarSectionLabel,
} from '@/components/ui/sidebar'
import {
  backupsPath,
  batchesPath,
  companiesPath,
  destroyUserSessionPath,
  fundsPath,
  industriesPath,
  foundersPath,
  limitedPartnersPath,
  positionsPath,
  rootPath,
  usersPath,
} from '@/lib/routes'
import { useSidebar } from '@/lib/sidebar'
import { cn } from '@/lib/utils'
import type { PageProps, UserRole } from '@/types/inertia'

type NavItem = {
  icon: ComponentType<{ className?: string }>
  label: string
  href?: string
  disabled?: boolean
  matchExact?: boolean
}

const NAV_BY_ROLE: Record<UserRole, { primary: NavItem[]; secondary: NavItem[] }> = {
  partner: {
    primary: [
      { icon: LayoutDashboard, label: 'Dashboard', href: rootPath(), matchExact: true },
      { icon: Briefcase, label: 'Funds', href: fundsPath() },
      { icon: Building2, label: 'Companies', href: companiesPath() },
      { icon: UserRound, label: 'Founders', href: foundersPath() },
      { icon: Users, label: 'Limited partners', href: limitedPartnersPath() },
    ],
    secondary: [
      { icon: UserCog, label: 'Users', href: usersPath() },
      { icon: GraduationCap, label: 'Batches', href: batchesPath() },
      { icon: Tags, label: 'Industries', href: industriesPath() },
      { icon: DatabaseBackup, label: 'Backups', href: backupsPath() },
      { icon: Settings, label: 'Settings', disabled: true },
    ],
  },
  lp: {
    primary: [
      { icon: LayoutDashboard, label: 'Dashboard', href: rootPath(), matchExact: true },
      { icon: Wallet, label: 'Positions', href: positionsPath() },
    ],
    secondary: [{ icon: Settings, label: 'Settings', disabled: true }],
  },
  founder: {
    primary: [
      { icon: LayoutDashboard, label: 'Dashboard', href: rootPath(), matchExact: true },
      { icon: Building2, label: 'Company', disabled: true },
    ],
    secondary: [{ icon: Settings, label: 'Settings', disabled: true }],
  },
}

function UserBlock({ collapsed }: { collapsed: boolean }) {
  const { auth } = usePage<PageProps>().props
  const user = auth.user
  if (!user) return null

  const initials = (user.first_name?.[0] ?? '') + (user.last_name?.[0] ?? '')

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
          {initials || user.email[0].toUpperCase()}
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Sign out"
          onClick={() => router.delete(destroyUserSessionPath())}
        >
          <LogOut />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 rounded-md p-2">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {initials || user.email[0].toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{user.full_name || user.email}</p>
        <p className="truncate text-xs capitalize text-muted-foreground">{user.role}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        aria-label="Sign out"
        onClick={() => router.delete(destroyUserSessionPath())}
      >
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  )
}

export function AppSidebar() {
  const { collapsed, toggle } = useSidebar()
  const { auth } = usePage<PageProps>().props
  const role = auth.user?.role ?? 'partner'
  const nav = NAV_BY_ROLE[role]

  return (
    <Sidebar collapsed={collapsed}>
      <SidebarHeader className={cn(collapsed && 'justify-center px-2')}>
        <PlatanusIsotype className="h-7 w-7 shrink-0" />
        {!collapsed && (
          <span className="flex-1 truncate text-sm font-semibold tracking-tight">
            Platanus Track
          </span>
        )}
      </SidebarHeader>

      <SidebarContent>
        <nav className="flex flex-col gap-0.5">
          {nav.primary.map((item) => (
            <SidebarItem key={item.label} collapsed={collapsed} {...item} />
          ))}

          <SidebarSectionLabel collapsed={collapsed}>System</SidebarSectionLabel>
          {nav.secondary.map((item) => (
            <SidebarItem key={item.label} collapsed={collapsed} {...item} />
          ))}
        </nav>
      </SidebarContent>

      <SidebarFooter className={cn('space-y-2', collapsed && 'flex flex-col items-center')}>
        <UserBlock collapsed={collapsed} />

        <div
          className={cn(
            'flex items-center gap-1',
            collapsed ? 'flex-col' : 'justify-between border-t border-border pt-2',
          )}
        >
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}
